import {
	createTheme,
	CssBaseline,
	responsiveFontSizes,
	ThemeOptions,
	ThemeProvider as MuiThemeProvider,
} from "@mui/material";
import deepmerge from "@mui/utils/deepmerge";
import {JetbrainsMonoFont} from "@variocube/app-ui/src/VCThemeProvider/JetbrainsMonoFont";
import {RobotoFont} from "@variocube/app-ui/src/VCThemeProvider/RobotoFont";
import React, {PropsWithChildren} from "react";
import {getFlavor} from "./flavor";

export function ThemeProvider({children}: PropsWithChildren<{}>) {
	const flavor = getFlavor().toLowerCase() as "smalox" | "boxlox" | "daylox";
	return (
		<MuiThemeProvider theme={themes[flavor]}>
			<CssBaseline />
			<RobotoFont />
			<JetbrainsMonoFont />
			{children}
		</MuiThemeProvider>
	);
}

const baseThemeOptions: ThemeOptions = {
	palette: {
		mode: "dark",
		error: {
			main: "#e62e2e",
		},
		success: {
			main: "#43a843",
		},
		warning: {
			main: "#e68739",
		},
		info: {
			main: "#294fcc",
		},
		text: {
			primary: "#ffffff",
		},
	},
	typography: {
		h1: {
			fontSize: 60,
			fontWeight: 300,
		},
		h2: {
			fontSize: 36,
			fontWeight: 500,
		},
		h3: {
			fontSize: 28,
			fontWeight: 500,
		},
		h4: {
			fontSize: 24,
			fontWeight: 500,
		},
		h5: {
			fontSize: 20,
			fontWeight: 500,
		},
		h6: {
			fontSize: 18,
			fontWeight: 500,
		},
		overline: {
			fontWeight: 500,
		},
	},
	components: {
		MuiLink: {
			defaultProps: {
				underline: "hover",
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: "unset",
				},
			},
		},
	},
};

function createFlavorTheme(overrides: ThemeOptions) {
	return responsiveFontSizes(createTheme(deepmerge(baseThemeOptions, overrides)));
}

const smaloxTheme = createFlavorTheme({
	palette: {
		primary: {
			main: "#FFED00",
		},
		secondary: {
			main: "#ccc452",
		},
		background: {
			default: "#0f0f08",
			paper: "#1f1f17",
		},
	},
});

const boxloxTheme = createFlavorTheme({
	palette: {
		primary: {
			main: "#c1f20f",
		},
		secondary: {
			main: "#d3f261",
		},
		background: {
			default: "#0f0f1a",
			paper: "#1e1e33",
		},
	},
	components: {
		MuiPaper: {
			styleOverrides: {
				root: {
					backdropFilter: "blur(20px)",
					backgroundColor: "rgba(52, 52, 71, 0.5)",
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					backgroundColor: "#000",
					backgroundImage:
						"linear-gradient(to left bottom, rgba(60, 61, 102, 0.25), rgba(46, 92, 102, 0.25))",
				},
			},
		},
	},
});

const dayloxTheme = createFlavorTheme({
	palette: {
		primary: {
			main: "#1ebdc1",
		},
		secondary: {
			main: "#62d4da",
		},
		background: {
			default: "#0f191a",
			paper: "#162526",
		},
	},
	components: {
		MuiPaper: {
			styleOverrides: {
				root: {
					backdropFilter: "blur(20px)",
					backgroundColor: "rgba(64, 64, 64, 0.25)",
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					backgroundColor: "#000",
					backgroundImage:
						"radial-gradient(at top left, rgba(21, 132, 135, 0.2), transparent 100%), radial-gradient(at top right, rgba(159, 81, 159, 0.2), transparent 100%)",
				},
			},
		},
	},
});

const themes = {
	smalox: smaloxTheme,
	boxlox: boxloxTheme,
	daylox: dayloxTheme,
};
